import {
    Dialog,
    Button,
    VStack,
} from "@chakra-ui/react";
import {useState, useEffect} from "react";
import {Field} from "../components/ui/field";
import {PasswordInput} from "../components/ui/password-input";
import {Toaster, toaster} from "./ui/toaster";

const baseUrl = process.env.REACT_APP_API_URL;

const PasswordChange = ({isOpen, onClose}) => {
    const [open, setOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);

    // 🔄 Sync internal state with external control (from Profile)
    useEffect(() => {
        if (typeof isOpen === "boolean") {
            setOpen(isOpen);
        }
    }, [isOpen]);

    const handleClose = () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setOpen(false);
        if (onClose) onClose();
    };

    const validatePassword = (password) => {
        const minLength = 8;
        const hasNumber = /\d/.test(password);
        const hasLetter = /[A-Za-z]/.test(password);
        const hasSpecialChar = /[@$!%*#?&]/.test(password);

        if (password.length < minLength) {
            return "La contraseña debe ser de al menos 8 caracteres.";
        }
        if (!hasNumber) {
            return "La contraseña debe tener al menos un número.";
        }
        if (!hasLetter) {
            return "La contraseña debe contener al menos una letra.";
        }
        if (!hasSpecialChar) {
            return "La contraseña debe contener al menos un caracter especial (@$!%*#?&-).";
        }
        return null;
    };

    const handleSubmit = async () => {
        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            toaster.error({
                title: "Error en la contraseña",
                description: passwordError,
                duration: 4000,
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            toaster.error({
                title: "Error",
                description: "Las contraseñas no coinciden.",
            });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${baseUrl}/auth/user/change-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
            });

            if (!response.ok) {
                throw new Error("Error al cambiar la contraseña");
            }

            toaster.success({
                title: "Éxito",
                description: "La contraseña se ha actualizado correctamente.",
            });

            handleClose();
        } catch (error) {
            toaster.error({
                title: "Error",
                description: error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={setOpen}>
            {/* 🔹 Only show trigger button if dialog isn’t externally controlled */}
            {!isOpen && (
                <Dialog.Trigger asChild>
                    <Button variant="outline">Cambiar Contraseña</Button>
                </Dialog.Trigger>
            )}

            <Dialog.Backdrop/>

            <Dialog.Positioner>
                <Dialog.Content>
                    <Dialog.CloseTrigger onClick={handleClose}/>
                    <Dialog.Header>
                        <Dialog.Title>Cambiar Contraseña</Dialog.Title>
                    </Dialog.Header>

                    <Dialog.Body>
                        <VStack spacing={4} mt={2}>
                            <Field label="Contraseña actual" required>
                                <PasswordInput
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="Contraseña actual"
                                />
                            </Field>

                            <Field label="Nueva contraseña" required>
                                <PasswordInput
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Nueva contraseña"
                                />
                            </Field>

                            <Field label="Confirmar nueva contraseña" required>
                                <PasswordInput
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirmar nueva contraseña"
                                />
                            </Field>
                        </VStack>
                    </Dialog.Body>

                    <Dialog.Footer>
                        <Button
                            colorPalette="green"
                            variant="subtle"
                            onClick={handleSubmit}
                            isLoading={loading}
                        >
                            Guardar
                        </Button>
                        <Button
                            variant="ghost"
                            colorPalette="red"
                            onClick={handleClose}
                        >
                            Cancelar
                        </Button>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog.Positioner>
        </Dialog.Root>
    );
};

export default PasswordChange;
